import { useCallback, useEffect, useMemo, useState } from "react"

import { DESKTOP_SIZE, useMediaQuery } from "shared/lib/useMediaQuery"
import { Action, ActionGroup, isActionGroup } from "shared/types/Action"

import { ActionsOverlayViewModel } from "../types/ActionOverlayViewModel"
import { ActionsOverlayActionType } from "../types/ActionsOverlayAction"

interface ActionsOverlayViewModelInput {
  id: string
  headline: string
  actions: (Action | ActionGroup)[]
  onCancel: () => void
}

export function useActionsOverlayViewModel({
  id,
  headline,
  actions,
  onCancel
}: ActionsOverlayViewModelInput): Omit<ActionsOverlayViewModel, "className"> {
  const isDesktop = useMediaQuery(DESKTOP_SIZE)
  const [actionStack, setActionStack] = useState<string[]>([])

  useEffect(() => {
    if (isDesktop) {
      onCancel()
    }
  }, [isDesktop])

  const pushGroup = useCallback(
    (groupId: string) => setActionStack(prev => [...prev, groupId]),
    []
  )
  const popGroup = useCallback(
    () => setActionStack(prev => prev.slice(0, -1)),
    []
  )

  const visibleActions = useMemo(() => {
    const asActionsArray = (group: ActionGroup): (Action | ActionGroup)[] =>
      group.actions as unknown as (Action | ActionGroup)[]

    const activeGroup = actionStack.reduce<ActionGroup | null>(
      (currentGroup, groupId) => {
        const source = currentGroup ? asActionsArray(currentGroup) : actions
        const found = source.find(a => isActionGroup(a) && a.id === groupId)
        return found && isActionGroup(found) ? found : currentGroup
      },
      null
    )

    const sourceActions = activeGroup ? asActionsArray(activeGroup) : actions

    const mapped = sourceActions.map(action =>
      isActionGroup(action)
        ? {
            ...action,
            actionType: ActionsOverlayActionType.GROUP,
            onClick: () => pushGroup(action.id)
          }
        : { ...action, actionType: ActionsOverlayActionType.ACTION }
    )

    if (!activeGroup) return mapped

    return [
      {
        id: `back-${activeGroup.id}`,
        key: `back-${activeGroup.id}`,
        label: activeGroup.label,
        icon: activeGroup.icon,
        actionType: ActionsOverlayActionType.BACK,
        onClick: popGroup
      },
      ...mapped
    ]
  }, [actions, actionStack, pushGroup, popGroup])

  return { id, headline, actions: visibleActions, onCancel, actionStack }
}
